import React, { useEffect, useState, useRef } from 'react';
import axiosClient from '../../actions/axios-client';

/** Reads `qr-code-text` from Axios plain headers or AxiosHeaders (axios 1.x). */
function getQrCodeTextHeader(headers) {
  if (!headers) return null;
  const raw =
    typeof headers.get === 'function'
      ? headers.get('qr-code-text')
      : headers['qr-code-text'];
  const s = typeof raw === 'string' ? raw.trim() : '';
  return s ? s : null;
}

function QrCaption({ href, children }) {
  if (href) {
    return (
      <figcaption>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#9b9b9b] underline underline-offset-2 hover:text-[#c4c4c4]"
        >
          {children}
        </a>
      </figcaption>
    );
  }
  return (
    <figcaption className="mt-[0.35rem] text-xs leading-normal text-red-700">
      {children}
    </figcaption>
  );
}

const figureClass =
  'm-0 max-w-[160px] text-center [&_img]:block [&_img]:max-h-[150px] [&_img]:max-w-[150px] [&_img]:h-auto [&_img]:w-auto [&_img]:object-contain [&_figcaption]:mt-[0.35rem] [&_figcaption]:text-xs [&_figcaption]:leading-normal [&_figcaption]:text-[#9b9b9b]';

export const FestivalPromotionImages = ({ festivalId, status }) => {
  const [publicUrl, setPublicUrl] = useState(null);
  const [privateUrl, setPrivateUrl] = useState(null);
  const [publicLinkHref, setPublicLinkHref] = useState(null);
  const [privateLinkHref, setPrivateLinkHref] = useState(null);
  const urlsRef = useRef([]);

  useEffect(() => {
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current = [];
    setPublicUrl(null);
    setPrivateUrl(null);
    setPublicLinkHref(null);
    setPrivateLinkHref(null);

    if (!festivalId || status === 'DELETED') {
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      const wantPublic = status === 'PUBLISHED' || status === 'PREVIEW';
      const wantPrivate = status === 'UNPUBLISHED' || status === 'PREVIEW';

      const fetches = [];
      if (wantPublic) {
        fetches.push(
          axiosClient
            .get(`/admin/festival/${festivalId}/qrcode`, {
              responseType: 'blob',
              headers: { Accept: 'image/png' },
            })
            .then((r) => ({
              key: 'public',
              blob: r.data,
              linkHref: getQrCodeTextHeader(r.headers),
            })),
        );
      }
      if (wantPrivate) {
        fetches.push(
          axiosClient
            .get(`/admin/festival/${festivalId}/previewCode`, {
              responseType: 'blob',
              headers: { Accept: 'image/png' },
            })
            .then((r) => ({
              key: 'private',
              blob: r.data,
              linkHref: getQrCodeTextHeader(r.headers),
            })),
        );
      }

      try {
        const results = await Promise.all(fetches);
        if (cancelled) return;
        for (const { key, blob, linkHref } of results) {
          const u = URL.createObjectURL(blob);
          urlsRef.current.push(u);
          if (key === 'public') {
            setPublicUrl(u);
            setPublicLinkHref(linkHref);
          }
          if (key === 'private') {
            setPrivateUrl(u);
            setPrivateLinkHref(linkHref);
          }
        }
      } catch {
        /* ignore — optional: surface toast */
      }
    };

    load();

    return () => {
      cancelled = true;
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      urlsRef.current = [];
    };
  }, [festivalId, status]);

  if (!festivalId || status === 'DELETED') {
    return null;
  }

  const showPublic = status === 'PUBLISHED' || status === 'PREVIEW';
  const showPrivate = status === 'UNPUBLISHED' || status === 'PREVIEW';

  if (!showPublic && !showPrivate) {
    return null;
  }

  const hasPublic = showPublic && !!publicUrl;
  const hasPrivate = showPrivate && !!privateUrl;
  const count = (hasPublic ? 1 : 0) + (hasPrivate ? 1 : 0);

  if (count === 0) {
    return <div className="flex min-h-0 min-w-0 flex-1 flex-col" />;
  }

  if (count === 1) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex w-full min-w-0 flex-1 items-start justify-center">
          {hasPublic && (
            <figure className={figureClass}>
              <img src={publicUrl} alt="QR code for public festival link" />
              <QrCaption href={publicLinkHref}>Public Link</QrCaption>
            </figure>
          )}
          {hasPrivate && (
            <figure className={figureClass}>
              <img src={privateUrl} alt="QR code for private preview" />
              <QrCaption href={privateLinkHref}>Private Preview</QrCaption>
            </figure>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex w-full min-w-0 flex-row items-start">
        <div className="min-w-0 flex-1" aria-hidden="true" />
        {hasPublic && (
          <figure className={figureClass}>
            <img src={publicUrl} alt="QR code for public festival link" />
            <QrCaption href={publicLinkHref}>Public Link</QrCaption>
          </figure>
        )}
        <div className="flex min-w-0 flex-1 items-start justify-end">
          {hasPrivate && (
            <figure className={figureClass}>
              <img src={privateUrl} alt="QR code for private preview" />
              <QrCaption href={privateLinkHref}>Private Preview</QrCaption>
            </figure>
          )}
        </div>
      </div>
    </div>
  );
};
